import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { validateId } from '~/repositories/utils';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { UploadFileOptions } from '~/repositories/uploadSessionRepository';
import type {
  LeaseChargeDto,
  LeaseChargeCreditDto,
  LeaseChargeLedgerDto,
  AddLeaseChargeDto,
  AddLeaseChargePaymentDto,
  AddLeaseChargeCreditDto,
  ReleaseLeaseBalanceDto,
  TransactionDto,
  ReturnLeaseChargePaymentDto,
  AdjustLeaseChargeDto,
  UpdateVendorBillDto,
  VendorBillDto,
  FileDto,
  LeaseLateFeeDto,
  AdjustLeaseChargeCreditDto,
  AddLeaseChargeFilesDto,
  UpdateLeaseChargeFilesDto,
  RemoveLeaseChargeFilesDto,
  AdjustLeaseChargePaymentDto,
} from '~/swagger/portal/Api';

const LEASES_URL = '/api/leases';

export class TransactionsRepository extends BaseRestRepository {
  public async getLeaseRentTransactions(leaseId: string, take?: number, before?: string): Promise<LeaseChargeLedgerDto> {
    const url = urlWithQuery(`${LEASES_URL}/${leaseId}/charges/ledger`, {
      take,
      before,
    });
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async setupCharge(leaseId: string, payload: AddLeaseChargeDto): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/charges`;
    const body = { ...payload };
    if (body.description?.trim().length === 0) {
      body.description = undefined;
    }
    await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  public async offlinePayment(leaseId: string, payload: AddLeaseChargePaymentDto, showErrorMessage = true): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/charges/payments`;
    await this.fetchWithAuth(
      url,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      showErrorMessage
    );
  }

  public async addCredit(leaseId: string, payload: AddLeaseChargeCreditDto): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/charges/credits`;
    await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async deleteTransaction(transactionId: string): Promise<void> {
    const url = `/api/transactions/${transactionId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async deleteBill(billId: string): Promise<void> {
    const url = `/api/vendors/bills/${billId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async payoutTenant(leaseId: string, request: ReleaseLeaseBalanceDto): Promise<TransactionDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/release`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.getJsonResponse(response);
  }

  public async markAsReturned(leaseId: string, paymentId: string, payload: ReturnLeaseChargePaymentDto): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/charges/payments/${paymentId}/return`;
    await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async updateTransaction(leaseId: string, chargeId: string, payload: AdjustLeaseChargeDto): Promise<TransactionDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/${chargeId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updateBill(billId: string, payload: UpdateVendorBillDto): Promise<VendorBillDto> {
    const url = `/api/vendors/bills/${billId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getTransactionsInvoice(leaseId: string): Promise<FileDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/ledger/invoice`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getLateFee(leaseId: string, lateFeeId: string): Promise<LeaseLateFeeDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/late-fees/${lateFeeId}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async deleteLateFee(leaseId: string, lateFeeId: string): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/charges/late-fees/${lateFeeId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async getCharge(leaseId: string, chargeId: string): Promise<LeaseChargeDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/${chargeId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async deleteCharge(leaseId: string, chargeId: string): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/charges/${chargeId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async deleteCredit(leaseId: string, creditId: string): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/charges/credits/${creditId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async getChargeCredit(leaseId: string, creditId: string): Promise<LeaseChargeCreditDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/credits/${creditId}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async updateChargeCredit(leaseId: string, creditId: string, payload: AdjustLeaseChargeCreditDto): Promise<TransactionDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/credits/${creditId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async uploadChargeFiles(leaseId: string, chargeId: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    validateId(leaseId, 'Lease');
    validateId(chargeId, 'Charge');
    if (files.length === 0) return [];

    return uploadSessionRepository.uploadFiles(`${LEASES_URL}/${leaseId}/charges/${chargeId}/files/upload-sessions`, files, opts);
  }

  public async addChargeFiles(leaseId: string, chargeId: string, payload: AddLeaseChargeFilesDto): Promise<LeaseChargeDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/${chargeId}/files`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updateChargeFiles(
    leaseId: string,
    chargeId: string,
    fileId: string,
    payload: UpdateLeaseChargeFilesDto
  ): Promise<LeaseChargeDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/${chargeId}/files/${fileId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteChargeFiles(leaseId: string, chargeId: string, payload: RemoveLeaseChargeFilesDto): Promise<LeaseChargeDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/${chargeId}/files`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getChargePayment(leaseId: string, paymentId: string): Promise<TransactionDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/payments/${paymentId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async updateChargePayment(leaseId: string, paymentId: string, payload: AdjustLeaseChargePaymentDto): Promise<TransactionDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/payments/${paymentId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteChargePayment(leaseId: string, paymentId: string): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/charges/payments/${paymentId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async getChargeTransfer(leaseId: string, transferId: string): Promise<TransactionDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/transfers/${transferId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async deleteChargeTransfer(leaseId: string, transferId: string): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/charges/transfers/${transferId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }
}

export const transactionsRepository = new TransactionsRepository();
